import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Lightbulb, Loader2, RefreshCw, Star, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const Suggestion = ({ s, onDecide }) => {
  const [rule, setRule] = useState(s.rule);
  const pending = s.status === "pending";
  return (
    <div className="rounded-2xl border border-[#27272A] bg-[#0D0D0F] p-4" data-testid={`coach-suggestion-${s.id}`}>
      {pending ? (
        <textarea value={rule} onChange={(e) => setRule(e.target.value)} rows={3} data-testid={`coach-suggestion-text-${s.id}`}
          className="w-full rounded-xl border border-[#27272A] bg-[#121215] p-3 text-[14px] text-[#E4E4E7] focus:border-[#D4AF37]/60 focus:outline-none" />
      ) : <p className="text-[14px] text-[#E4E4E7]">{s.rule}</p>}
      {s.reason && <p className="mt-2 text-[13px] text-[#8A8E96]">Perché: {s.reason}</p>}
      <div className="mt-3 flex gap-2">
        {pending && (
          <button onClick={() => onDecide(s.id, "approved", rule)} data-testid={`coach-suggestion-approve-${s.id}`} className="btn-gold flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px]">
            <Check size={14} /> Approva
          </button>
        )}
        <button onClick={() => onDecide(s.id, "rejected")} data-testid={`coach-suggestion-reject-${s.id}`} className="flex items-center gap-1.5 rounded-full border border-[#3F3F46] px-4 py-2 text-[13px] text-[#B4B4BC]">
          <X size={14} /> {pending ? "Scarta" : "Disattiva"}
        </button>
      </div>
    </div>
  );
};

export const CoachLearning = () => {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => api.get("/admin/coach-learning").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, status, rule) => {
    try {
      await api.post(`/admin/coach-suggestions/${id}`, { status, rule });
      toast.success(status === "approved" ? "Regola approvata: Mino la usa da subito" : "Regola rimossa");
      load();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/admin/coach-learning/run");
      toast.success(`Report creato: ${r.stats.domande} domande analizzate. Email inviata.`);
      load();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };
  if (!data) return null;
  const pending = data.suggestions.filter((s) => s.status === "pending");
  const approved = data.suggestions.filter((s) => s.status === "approved");
  const last = data.reports[0];
  return (
    <div className="space-y-4 border-t border-[#27272A] pt-5" data-testid="coach-learning">
      <p className="flex items-center gap-2 font-semibold text-[#FAF9F6]"><Lightbulb size={18} className="text-[#D4AF37]" /> Mino impara dalle domande</p>
      <p className="text-sm text-[#8A8E96]">Ogni lunedì alle 8:00 ricevi un report via email con i temi più chiesti e le regole suggerite. Le approvi qui; quelle approvate Mino le usa subito.</p>
      {last && (
        <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-4 text-sm text-[#D4D4D8]" data-testid="coach-learning-last-report">
          <p className="text-[#8A8E96]">Ultimo report · {new Date(last.created_at).toLocaleDateString("it-IT")}</p>
          <p className="mt-1">{last.stats.domande} domande da {last.stats.clienti} clienti · <ThumbsUp size={13} className="inline" /> {last.stats.utili} · <ThumbsDown size={13} className="inline" /> {last.stats.non_utili}</p>
          {last.topics?.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-5">{last.topics.map((t) => <li key={t.tema}><b>{t.tema}</b> ({t.quante})</li>)}</ul>}
        </div>
      )}
      <button onClick={runNow} disabled={running} data-testid="coach-learning-run-button" className="btn-outline-gold flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-sm">
        {running ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} {running ? "Analizzo le domande…" : "Crea il report adesso"}
      </button>
      <p className="font-semibold text-[#FAF9F6]">Da approvare ({pending.length})</p>
      {pending.length ? pending.map((s) => <Suggestion key={s.id} s={s} onDecide={decide} />) : <p className="text-sm text-[#8A8E96]">Nessuna regola in attesa.</p>}
      <p className="font-semibold text-[#FAF9F6]">Regole attive apprese ({approved.length})</p>
      {approved.length ? approved.map((s) => <Suggestion key={s.id} s={s} onDecide={decide} />) : <p className="text-sm text-[#8A8E96]">Nessuna regola appresa per ora.</p>}
      <Examples />
    </div>
  );
};

const Examples = () => {
  const [ex, setEx] = useState(null);
  const load = useCallback(() => api.get("/admin/coach-examples").then(({ data }) => setEx(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!ex) return null;
  const remove = (id) => api.delete(`/admin/coach-examples/${id}`).then(() => { toast.success("Risposta modello rimossa"); load(); });
  return (
    <div className="space-y-3 pt-2" data-testid="coach-examples">
      <p className="flex items-center gap-2 font-semibold text-[#FAF9F6]"><Star size={16} className="text-[#D4AF37]" /> Risposte modello ({ex.examples.length})</p>
      <p className="text-sm text-[#8A8E96]">Scrivi a Coach Mino dal tuo account admin e tocca "Salva come modello" sotto una risposta perfetta. Mino imita le ultime {ex.active_max}.</p>
      {ex.examples.map((e, i) => (
        <div key={e.id} className={`rounded-2xl border border-[#27272A] bg-[#0D0D0F] p-4 text-[14px] ${i >= ex.active_max ? "opacity-50" : ""}`} data-testid={`coach-example-${e.id}`}>
          <p className="text-[#8A8E96]">Domanda: <i>{e.question}</i></p>
          <p className="mt-1.5 whitespace-pre-wrap text-[#E4E4E7]">{e.answer}</p>
          <button onClick={() => remove(e.id)} data-testid={`coach-example-delete-${e.id}`} className="mt-2 flex items-center gap-1.5 rounded-full border border-[#3F3F46] px-3 py-1.5 text-[12px] text-[#B4B4BC]">
            <X size={13} /> Rimuovi
          </button>
        </div>
      ))}
    </div>
  );
};
